// Plena Growth — Development Plan PDF Generator
// Uses jsPDF + jspdf-autotable. Pure client-side.
// Türkçe karakter desteği için DejaVu Sans fontu CDN'den runtime embed edilir (~340KB).
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const FONT_URL = "https://cdn.jsdelivr.net/npm/dejavu-fonts-ttf@2.37.3/ttf/DejaVuSans.ttf";
const FONT_BOLD_URL = "https://cdn.jsdelivr.net/npm/dejavu-fonts-ttf@2.37.3/ttf/DejaVuSans-Bold.ttf";

// In-module cache to avoid re-downloading on subsequent PDFs
let _fontCache = null;

function arrayBufferToBase64(buffer) {
  // Chunked conversion avoids "Maximum call stack size exceeded" on large buffers
  const bytes = new Uint8Array(buffer);
  let binary = "";
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

async function loadTurkishFont() {
  if (_fontCache) return _fontCache;
  const [regular, bold] = await Promise.all([
    fetch(FONT_URL).then((r) => r.arrayBuffer()),
    fetch(FONT_BOLD_URL).then((r) => r.arrayBuffer()),
  ]);
  _fontCache = {
    regular: arrayBufferToBase64(regular),
    bold: arrayBufferToBase64(bold),
  };
  return _fontCache;
}

function setupFont(doc, fonts) {
  doc.addFileToVFS("DejaVuSans.ttf", fonts.regular);
  doc.addFont("DejaVuSans.ttf", "DejaVu", "normal");
  doc.addFileToVFS("DejaVuSans-Bold.ttf", fonts.bold);
  doc.addFont("DejaVuSans-Bold.ttf", "DejaVu", "bold");
  doc.setFont("DejaVu", "normal");
}

// Brand palette in Plena Growth Light Mode
const SKY = [14, 116, 144]; // sky-700 ish
const SLATE_900 = [15, 23, 42];
const SLATE_600 = [71, 85, 105];
const SLATE_400 = [148, 163, 184];
const SLATE_100 = [241, 245, 249];
const AMBER = [180, 83, 9];
const EMERALD = [4, 120, 87];

const PAGE_W = 210; // A4 mm
const MARGIN = 14;

function setText(doc, color = SLATE_900, size = 10, style = "normal") {
  doc.setTextColor(...color);
  doc.setFontSize(size);
  doc.setFont("DejaVu", style);
}

function sectionTitle(doc, text, y) {
  setText(doc, SKY, 8, "bold");
  doc.text(text.toUpperCase(), MARGIN, y);
  doc.setDrawColor(...SLATE_100);
  doc.setLineWidth(0.4);
  doc.line(MARGIN, y + 1.5, PAGE_W - MARGIN, y + 1.5);
  return y + 7;
}

function paragraph(doc, text, y, opts = {}) {
  setText(doc, opts.color || SLATE_600, opts.size || 9.5, opts.style || "normal");
  const wrapped = doc.splitTextToSize(text, PAGE_W - MARGIN * 2);
  doc.text(wrapped, MARGIN, y);
  return y + wrapped.length * 4.6 + (opts.gap ?? 2);
}

function ensurePage(doc, y, needed = 30) {
  if (y + needed > 285) {
    doc.addPage();
    return 20;
  }
  return y;
}

const typeLabel = (t) => ({
  course: "Eğitim",
  project: "Proje",
  book: "Kitap",
  certification: "Sertifika",
  coaching: "Koçluk",
}[t] || t);

export async function generateDevelopmentPlanPdf(plan) {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  // Embed Turkish-capable DejaVu font (Roboto family supports Latin-Extended-A: ş, ğ, ı, ö, ü, ç)
  try {
    const fonts = await loadTurkishFont();
    setupFont(doc, fonts);
  } catch (e) {
    // Fallback to Helvetica (will not render Turkish chars but allows download)
    console.warn("DejaVu font load failed, falling back to Helvetica", e);
  }

  // === Cover header ===
  // Sky band
  doc.setFillColor(...SKY);
  doc.rect(0, 0, PAGE_W, 38, "F");
  setText(doc, [255, 255, 255], 9, "bold");
  doc.text("PLENA GROWTH · YETENEK ZEKASI", MARGIN, 12);
  setText(doc, [255, 255, 255], 18, "bold");
  doc.text("Gelişim Planı", MARGIN, 24);
  setText(doc, [219, 234, 254], 9, "normal");
  doc.text(
    `Oluşturma: ${new Date(plan.generated_at).toLocaleDateString("tr-TR", { year: "numeric", month: "long", day: "numeric" })}`,
    MARGIN,
    32,
  );

  let y = 50;

  // === Employee snapshot card ===
  doc.setFillColor(...SLATE_100);
  doc.roundedRect(MARGIN, y, PAGE_W - MARGIN * 2, 28, 2, 2, "F");
  setText(doc, SLATE_900, 14, "bold");
  doc.text(plan.employee.name, MARGIN + 4, y + 9);
  setText(doc, SLATE_600, 10, "normal");
  doc.text(`${plan.employee.title} · ${plan.employee.department}`, MARGIN + 4, y + 15);
  setText(doc, SLATE_400, 8.5, "normal");
  doc.text(
    `Kıdem: ${plan.employee.tenure_years ?? "-"} yıl  ·  Performans: ${plan.employee.performance_rating ?? "-"}  ·  Bağlılık: ${plan.employee.engagement_score ?? "-"}/100`,
    MARGIN + 4,
    y + 21,
  );
  // Readiness pill on right
  const readiness = plan.skill_gap.readiness_score;
  doc.setFillColor(...SKY);
  doc.roundedRect(PAGE_W - MARGIN - 38, y + 6, 34, 16, 2, 2, "F");
  setText(doc, [255, 255, 255], 7, "normal");
  doc.text("HAZIR OLMA", PAGE_W - MARGIN - 36, y + 11);
  setText(doc, [255, 255, 255], 16, "bold");
  doc.text(`%${readiness}`, PAGE_W - MARGIN - 36, y + 19);
  y += 36;

  // === Career path ===
  y = sectionTitle(doc, "Hedef Rol & Kariyer Yolu", y);
  setText(doc, SLATE_900, 11, "bold");
  doc.text(`${plan.current_role.title}  →  ${plan.target_role.title}`, MARGIN, y);
  setText(doc, SLATE_400, 8.5, "normal");
  doc.text(`(${plan.target_role.career_band})`, MARGIN + doc.getTextWidth(`${plan.current_role.title}  →  ${plan.target_role.title}  `), y);
  y += 5;

  if (plan.career_paths?.length) {
    autoTable(doc, {
      startY: y,
      margin: { left: MARGIN, right: MARGIN },
      head: [["Önerilen Rol", "Eşleşme", "Süre", "Tip"]],
      body: plan.career_paths.map((p) => [
        p.role_title,
        `%${p.match_score}`,
        `${p.time_to_ready_months} ay`,
        p.is_promotion ? "Terfi" : "Yatay geçiş",
      ]),
      theme: "grid",
      headStyles: { fillColor: SKY, textColor: 255, fontSize: 9, halign: "left", font: "DejaVu", fontStyle: "bold" },
      bodyStyles: { fontSize: 9, textColor: SLATE_900, font: "DejaVu" },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      styles: { cellPadding: 2.2 },
    });
    y = doc.lastAutoTable.finalY + 6;
  }

  y = ensurePage(doc, y, 40);

  // === Skill gap ===
  y = sectionTitle(doc, "Yetkinlik Açıkları", y);
  if (plan.skill_gap.gaps?.length) {
    autoTable(doc, {
      startY: y,
      margin: { left: MARGIN, right: MARGIN },
      head: [["Beceri", "Mevcut", "Hedef", "Fark"]],
      body: plan.skill_gap.gaps.map((g) => [g.skill_name, `${g.current}/5`, `${g.target}/5`, `+${g.delta}`]),
      theme: "striped",
      headStyles: { fillColor: AMBER, textColor: 255, fontSize: 9, font: "DejaVu", fontStyle: "bold" },
      bodyStyles: { fontSize: 9, textColor: SLATE_900, font: "DejaVu" },
      styles: { cellPadding: 2.2 },
    });
    y = doc.lastAutoTable.finalY + 4;
  }

  if (plan.skill_gap.aligned?.length) {
    setText(doc, EMERALD, 8.5, "bold");
    doc.text("Uyumlu beceriler:", MARGIN, y);
    setText(doc, SLATE_600, 8.5, "normal");
    const aligned = plan.skill_gap.aligned.join(" · ");
    const wrap = doc.splitTextToSize(aligned, PAGE_W - MARGIN * 2 - 36);
    doc.text(wrap, MARGIN + 36, y);
    y += wrap.length * 4 + 4;
  }

  y = ensurePage(doc, y, 45);

  // === Mentor ===
  y = sectionTitle(doc, "Mentor Önerisi", y);
  if (plan.mentor) {
    setText(doc, SLATE_900, 11, "bold");
    doc.text(plan.mentor.name, MARGIN, y);
    setText(doc, SLATE_400, 9, "normal");
    doc.text(`  ·  Eşleşme %${plan.mentor.match_score}`, MARGIN + doc.getTextWidth(plan.mentor.name), y);
    y += 5;
    setText(doc, SLATE_600, 9, "normal");
    doc.text(plan.mentor.title, MARGIN, y);
    y += 5;
    if (plan.mentor.complementary_skills?.length) {
      setText(doc, AMBER, 8.5, "bold");
      doc.text("Sana katacağı: ", MARGIN, y);
      setText(doc, SLATE_900, 8.5, "normal");
      const skills = plan.mentor.complementary_skills.join(" · ");
      const wrap = doc.splitTextToSize(skills, PAGE_W - MARGIN * 2 - 24);
      doc.text(wrap, MARGIN + 24, y);
      y += wrap.length * 4 + 4;
    }
  } else {
    y = paragraph(doc, "Şu anda uygun mentor önerisi bulunmuyor.", y, { color: SLATE_400 });
  }

  y = ensurePage(doc, y, 60);

  // === Learning plan ===
  y = sectionTitle(doc, "Öğrenme Yolu", y);
  setText(doc, SLATE_600, 9, "normal");
  doc.text(
    `${plan.learning.total_items} kaynak  ·  ${plan.learning.total_duration_weeks} hafta  ·  Tahmini hazır olma: %${plan.skill_gap.readiness_score} → %${plan.learning.projected_readiness}  (+%${plan.learning.projected_boost})`,
    MARGIN,
    y,
  );
  y += 6;

  for (const grp of plan.learning.groups || []) {
    y = ensurePage(doc, y, 30);
    setText(doc, SLATE_900, 9.5, "bold");
    doc.text(`▸ ${grp.skill_name}`, MARGIN, y);
    y += 3;
    autoTable(doc, {
      startY: y,
      margin: { left: MARGIN, right: MARGIN },
      head: [["Kaynak", "Tip", "Sağlayıcı", "Süre"]],
      body: grp.items.map((i) => [i.title, typeLabel(i.type), i.provider, `${i.duration_weeks} hf`]),
      theme: "plain",
      headStyles: { fillColor: SLATE_100, textColor: SLATE_900, fontSize: 8, font: "DejaVu", fontStyle: "bold" },
      bodyStyles: { fontSize: 8.5, textColor: SLATE_900, font: "DejaVu" },
      styles: { cellPadding: 1.6 },
    });
    y = doc.lastAutoTable.finalY + 3;
  }

  y = ensurePage(doc, y, 35);

  // === 90-day Action Plan ===
  y = sectionTitle(doc, "90 Günlük Aksiyon Planı", y);
  if (plan.action_plan_90d?.length) {
    autoTable(doc, {
      startY: y,
      margin: { left: MARGIN, right: MARGIN },
      head: [["Vade", "Aksiyon", "Kapsam"]],
      body: plan.action_plan_90d.map((a) => [a.horizon, a.title, a.skill]),
      theme: "grid",
      headStyles: { fillColor: SKY, textColor: 255, fontSize: 9, font: "DejaVu", fontStyle: "bold" },
      bodyStyles: { fontSize: 9, textColor: SLATE_900, font: "DejaVu" },
      styles: { cellPadding: 2.2 },
    });
    y = doc.lastAutoTable.finalY + 4;
  }

  // === Footer on every page ===
  const total = doc.internal.getNumberOfPages();
  for (let i = 1; i <= total; i++) {
    doc.setPage(i);
    setText(doc, SLATE_400, 7.5, "normal");
    doc.text(
      `Plena Growth · ${plan.employee.name} · Gelişim Planı`,
      MARGIN,
      292,
    );
    doc.text(`Sayfa ${i} / ${total}`, PAGE_W - MARGIN, 292, { align: "right" });
  }

  const safeName = plan.employee.name.replace(/\s+/g, "_");
  doc.save(`Gelisim_Plani_${safeName}.pdf`);
}
